import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import TCITestForm from './TCITestFormLink';
import './SurveyComponent.css';

const FETCH_POST_LINK = gql`
  query ($postId: ID!) {
    getPosLink(postId: $postId) {
      id
      estado
      destinatario
      informacion
      emailOwner
      linkOwner
      createdAt
    }
  }
`;

const SurveyComponent = () => {
  const { postId } = useParams();
  const { data, loading: linkLoading, error: linkError, refetch: refetchLink } = useQuery(
    FETCH_POST_LINK,
    { variables: { postId } }
  );
  const link = data?.getPosLink;

  // UI state
  const [currentStep, setCurrentStep] = useState(0);
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [dob, setDob] = useState('');
  const [mailUserLink, setMailUserLink] = useState('');
  const [showLoading, setShowLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [isDisabledAfterSubmit, setIsDisabledAfterSubmit] = useState(false);

  // Spinner phases
  const spinnerPhases = [
    'Pensando… procesando patrón conductual',
    'Analizando resultados…',
    'Guardando información…'
  ];

  // Si ya fue completado el link
  useEffect(() => {
    if (!linkLoading && link?.estado === 'completado') {
      setShowLoading(true);
      setLoadingMessage(<span className="loading-text">El link ha sido usado</span>);
    }
  }, [linkLoading, link]);

  const handleTestComplete = async () => {
    setIsDisabledAfterSubmit(true);
    setShowLoading(true);
    for (const phase of spinnerPhases) {
      setLoadingMessage(<span className="loading-text">{phase}</span>);
      await new Promise(res => setTimeout(res, 1500));
    }
    const { data: newData } = await refetchLink();
    const estado = newData.getPosLink.estado;
    if (estado === 'completado') {
      setLoadingMessage(<span className="loading-text">Gracias por completar el test.</span>);
      setTimeout(() => {
        setShowLoading(false);
        window.close();
      }, 2000);
    } else {
      setLoadingMessage(<span className="loading-text">Ocurrió un error al procesar el test.</span>);
      setTimeout(() => setShowLoading(false), 2000);
    }
  };

  // Definimos los pasos, ahora con Email antes del Test
  const steps = [
    'Nombre',
    'Apellido',
    'Fecha de Nacimiento',
    'Email',
    'Test TCI'
  ];

  // Validación para habilitar Siguiente
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isNextEnabled = () => {
    switch (currentStep) {
      case 0: return name.trim() !== '';
      case 1: return surname.trim() !== '';
      case 2: return dob.trim() !== '';
      case 3: return emailRegex.test(mailUserLink);
      default: return true;
    }
  };

  if (linkLoading) return <div className='loading-text'>Cargando enlace…</div>;
  if (linkError) return <div className='loading-text'>Error al cargar enlace</div>;

  if (link.estado === 'completado') {
    return (
      <div className="survey-containerLink">
        <div className="spinner-container">
          {loadingMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="survey-containerLink">
      {showLoading && (
        <div className="spinner-container">
          {loadingMessage}
        </div>
      )}

      {!showLoading && (
        <>
          <div className="survey-steps">
            {steps.map((_, idx) => (
              <span
                key={idx}
                className={`step-dot ${idx <= currentStep ? 'completed' : ''}`}
              />
            ))}
          </div>

          <div className="survey-content">
            {currentStep === 0 && (
              <div className="input-step">
                <label>Nombre:</label>
                <input
                  type="text"
                  placeholder="Ingrese su nombre"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </div>
            )}
            {currentStep === 1 && (
              <div className="input-step">
                <label>Apellido:</label>
                <input
                  type="text"
                  placeholder="Ingrese su apellido"
                  value={surname}
                  onChange={e => setSurname(e.target.value)}
                />
              </div>
            )}
            {currentStep === 2 && (
              <div className="input-step">
                <label>Fecha de Nacimiento:</label>
                <input
                  type="date"
                  value={dob}
                  onChange={e => setDob(e.target.value)}
                />
              </div>
            )}
            {currentStep === 3 && (
              <div className="input-step">
                <label>Email:</label>
                <input
                  type="email"
                  placeholder="usuario@ejemplo.com"
                  value={mailUserLink}
                  onChange={e => setMailUserLink(e.target.value)}
                />
                {!emailRegex.test(mailUserLink) && mailUserLink && (
                  <span className="error-text">Formato de email inválido</span>
                )}
              </div>
            )}
            {currentStep === 4 && (
              <div className="test-step">
                <TCITestForm
                  postId={postId}
                  username={link.linkOwner.replace(/[^a-zA-Z0-9]/g, '')}
                  email={mailUserLink}
                  emailOwner={link.emailOwner}
                  nombre={name}
                  apellido={surname}
                  fecha={dob}
                  onCompleted={handleTestComplete}
                  setIsDisabledAfterSubmit={setIsDisabledAfterSubmit}
                />
              </div>
            )}
          </div>

          {!showLoading && link?.estado !== 'completado' && (
            <div className="survey-navigation">
              {currentStep > 0 && (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="navi-button"
                  disabled={isDisabledAfterSubmit}
                >
                  Anterior
                </button>
              )}
              {currentStep < steps.length - 1 && (
                <button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className="navi-button"
                  disabled={!isNextEnabled()}
                >
                  Siguiente
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SurveyComponent;

